import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  CheckCircle2,
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  FileSpreadsheet,
  X,
} from 'lucide-react';
import type { ImportCommitResponse, ImportMode } from '@forest/contracts';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import {
  useShapefileImportParse,
  useImportCommit,
  type ShapefileParseResponse,
} from './useImport';
import { useUiStore } from '@/stores/uiStore';
import { apiError } from '@/lib/api-client';
import { cn } from '@/lib/cn';
import { ImportColumnCheck, ImportProblems } from './ImportProblems';
import { ShapefileEditStep, toCommitEdits, type ShpDraftEdits } from './ShapefileEditStep';

type Step = 'upload' | 'preview' | 'complete' | 'commit' | 'done';
const STEPS: Step[] = ['upload', 'preview', 'complete', 'commit'];

const REQUIRED_EXTS = ['shp', 'dbf'];
const ACCEPTED_EXTS = ['shp', 'dbf', 'prj', 'shx'];

function extOf(name: string): string {
  return name.toLowerCase().split('.').pop() ?? '';
}

/**
 * Self-contained 3-step import flow for ESRI Shapefile parcel data
 * (PNERP UCF_PNERP_2022_2025.shp and lookalikes). Distinct from the
 * xlsx wizard because the request payload is multi-file and the
 * preview row shape is different (extra parcela / UA / codPrimarie
 * fields + COD_UNIC).
 */
export function ShapefileImportPanel() {
  const { t } = useTranslation(['import', 'common']);
  const navigate = useNavigate();
  const pushToast = useUiStore((s) => s.pushToast);
  const parseMut = useShapefileImportParse();
  const commitMut = useImportCommit();

  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<ShapefileParseResponse | null>(null);
  const [committed, setCommitted] = useState<ImportCommitResponse | null>(null);
  const [mode, setMode] = useState<ImportMode>('skip_invalid');
  /** Year for rows whose file has no year column (MACP-style layers). */
  const [defaultYear, setDefaultYear] = useState('');
  /** The editing step's changes, per row; sent with the commit. */
  const [edits, setEdits] = useState<ShpDraftEdits>({});

  const stepIndex = STEPS.indexOf(step);

  const addFiles = useCallback((list: FileList | File[] | null) => {
    if (!list) return;
    const incoming = Array.from(list).filter((f) => ACCEPTED_EXTS.includes(extOf(f.name)));
    if (incoming.length === 0) return;
    setFiles((prev) => {
      const byExt = new Map<string, File>();
      for (const f of prev) byExt.set(extOf(f.name), f);
      // Newer drops overwrite same-extension files so the user can
      // re-pick a single sidecar without clearing everything.
      for (const f of incoming) byExt.set(extOf(f.name), f);
      return Array.from(byExt.values());
    });
  }, []);

  const removeFile = (target: File) => {
    setFiles((prev) => prev.filter((f) => f !== target));
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const haveRequired = REQUIRED_EXTS.every((ext) =>
    files.some((f) => extOf(f.name) === ext),
  );

  const onParse = async () => {
    if (!haveRequired) {
      pushToast({
        variant: 'error',
        message: t('import:shp.missingFiles', {
          defaultValue: 'Lipsesc .shp și/sau .dbf',
        }),
      });
      return;
    }
    try {
      const y = Number(defaultYear);
      const result = await parseMut.mutateAsync({
        files,
        defaultYear: Number.isInteger(y) && y >= 1990 && y <= 2100 ? y : undefined,
      });
      setParsed(result);
      setEdits({});
      setStep('preview');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  const onCommit = async () => {
    if (!parsed) return;
    try {
      const commitEdits = toCommitEdits(parsed.editable ?? [], edits);
      const result = await commitMut.mutateAsync({
        batchId: parsed.batchId,
        mode,
        ...(Object.keys(commitEdits).length > 0 ? { edits: commitEdits } : {}),
      });
      setCommitted(result);
      setStep('done');
    } catch (e) {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    }
  };

  return (
    <div className="space-y-6">
      {/* Step indicator */}
      {step !== 'done' && (
        <div className="flex items-center justify-center gap-1">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-all',
                  i < stepIndex
                    ? 'bg-forest-500 text-white'
                    : i === stepIndex
                      ? 'bg-forest-600 text-white ring-4 ring-forest-100 dark:ring-forest-900/40'
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
                )}
              >
                {i < stepIndex ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={cn(
                    'mx-2 h-px w-8',
                    i < stepIndex
                      ? 'bg-forest-400'
                      : 'bg-slate-200 dark:bg-slate-700',
                  )}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Step 1: Upload */}
      {step === 'upload' && (
        <div className="space-y-4">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            className={cn(
              'relative flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 transition-all',
              dragOver
                ? 'border-forest-500 bg-forest-50 dark:bg-forest-900/20'
                : files.length > 0
                  ? 'border-forest-300 bg-forest-50/50 dark:border-forest-700 dark:bg-forest-900/15'
                  : 'border-slate-300 bg-slate-50 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800/60 dark:hover:border-slate-500',
            )}
          >
            <Upload className="mb-3 h-10 w-10 text-slate-400 dark:text-slate-500" />
            <p className="text-sm font-medium text-slate-600 dark:text-slate-300">
              {t('shp.dropHint', {
                defaultValue: 'Trage și plasează .shp + .dbf + .prj + .shx',
              })}
            </p>
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              {t('shp.dropHintSecondary', {
                defaultValue:
                  '.shp și .dbf sunt obligatorii; .prj și .shx sunt opționale (reproiectare automată MOLDREF99 → WGS84)',
              })}
            </p>
            <input
              type="file"
              multiple
              accept=".shp,.dbf,.prj,.shx"
              onChange={(e) => addFiles(e.target.files)}
              className="absolute inset-0 cursor-pointer opacity-0"
            />
          </div>

          {/* Selected files list */}
          {files.length > 0 && (
            <Card>
              <CardBody className="p-0">
                <ul className="divide-y divide-slate-100 dark:divide-slate-800">
                  {files.map((f) => {
                    const ext = extOf(f.name);
                    const required = REQUIRED_EXTS.includes(ext);
                    return (
                      <li
                        key={f.name}
                        className="flex items-center justify-between px-4 py-2 text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <FileSpreadsheet className="h-4 w-4 text-forest-500" />
                          <span className="font-medium text-slate-800 dark:text-slate-100">
                            {f.name}
                          </span>
                          <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            {ext}
                            {required ? ' · ' + t('shp.required', { defaultValue: 'necesar' }) : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                          <span>{(f.size / 1024).toFixed(1)} KB</span>
                          <button
                            type="button"
                            onClick={() => removeFile(f)}
                            className="rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            aria-label={t('common:action.delete')}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </CardBody>
            </Card>
          )}

          <div className="flex flex-wrap items-end justify-between gap-3">
            <label className="text-xs text-slate-600 dark:text-slate-300">
              <span className="mb-1 block font-semibold">
                {t('shp.defaultYear', { defaultValue: 'Anul, pentru rândurile fără Anul_proie' })}
              </span>
              <input
                type="number"
                min={1990}
                max={2100}
                inputMode="numeric"
                value={defaultYear}
                onChange={(e) => setDefaultYear(e.target.value)}
                placeholder={t('shp.defaultYearPlaceholder', { defaultValue: 'ex. 2025' })}
                className="h-8 w-32 rounded-md border border-slate-300 bg-white px-2 text-sm dark:border-slate-600 dark:bg-slate-800"
              />
              <span className="mt-1 block text-[11px] text-slate-400">
                {t('shp.defaultYearHint', {
                  defaultValue:
                    'Opțional. Gol: se folosește anul din coloana Data, dacă există. Anul_proie din fișier are întâietate.',
                })}
              </span>
            </label>
            <Button onClick={onParse} loading={parseMut.isPending} disabled={!haveRequired}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 'preview' && parsed && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label={t('preview.rows', { defaultValue: 'Rânduri' })}
              value={parsed.validCount + parsed.invalidCount}
              color="slate"
            />
            <StatCard
              label={t('preview.valid', { defaultValue: 'Valide' })}
              value={parsed.validCount}
              color="green"
            />
            <StatCard
              label={t('preview.invalid', { defaultValue: 'Invalide' })}
              value={parsed.invalidCount}
              color={parsed.invalidCount > 0 ? 'red' : 'slate'}
            />
            <StatCard
              label={t('shp.reprojected', { defaultValue: 'Reproiectat' })}
              value={parsed.reprojected ? 'WGS84' : '—'}
              color={parsed.reprojected ? 'green' : 'red'}
            />
          </div>

          {!parsed.reprojected && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {t('shp.reprojectWarning', {
                defaultValue:
                  '.prj nu a fost recunoscut (așteptat MOLDREF99). Coordonatele au rămas neproiectate — încărcați .prj pentru rezultat corect.',
              })}
            </div>
          )}

          {/* What the file was recognised as, and what we did to it. */}
          <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm dark:border-slate-700 dark:bg-slate-800/50">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded bg-forest-100 px-2 py-0.5 text-xs font-semibold text-forest-700 dark:bg-forest-900/40 dark:text-forest-300">
                {parsed.layerKind === 'teren'
                  ? t('shp.layerTeren', { defaultValue: 'Strat terenuri (poligoane părinte)' })
                  : t('shp.layerUcf', { defaultValue: 'Strat UCF (subdiviziuni)' })}
              </span>
              {parsed.coveredByUcf > 0 && (
                <span className="text-xs text-slate-600 dark:text-slate-300">
                  {t('shp.coveredByUcf', {
                    defaultValue:
                      '{{count}} rânduri sărite — terenul este deja acoperit de poligoane UCF',
                    count: parsed.coveredByUcf,
                  })}
                </span>
              )}
            </div>
            <ul className="space-y-0.5 text-xs text-slate-600 dark:text-slate-300">
              <li>
                {t('shp.normEntitate', {
                  defaultValue:
                    'Entitate silvică: {{matched}} normalizate, {{unmatched}} păstrate ca în fișier',
                  matched: parsed.normalized.entitateMatched,
                  unmatched: parsed.normalized.entitateUnmatched,
                })}
              </li>
              <li>
                {t('shp.normPrimarie', {
                  defaultValue:
                    'Primărie după cod: {{matched}} găsite în registru ({{renamed}} redenumite, {{raion}} raioane corectate), {{unmatched}} fără potrivire',
                  matched: parsed.normalized.primarieMatched,
                  renamed: parsed.normalized.primarieRenamed,
                  raion: parsed.normalized.raionRenamed,
                  unmatched: parsed.normalized.primarieUnmatched,
                })}
              </li>
              {parsed.yearSources &&
                parsed.yearSources.default + parsed.yearSources.data > 0 && (
                  <li>
                    {t('shp.yearSources', {
                      defaultValue:
                        'Anul: {{file}} din fișier, {{def}} din anul introdus la import, {{data}} din coloana Data',
                      file: parsed.yearSources.file,
                      def: parsed.yearSources.default,
                      data: parsed.yearSources.data,
                    })}
                  </li>
                )}
              {(parsed.codesFromName ?? 0) > 0 && (
                <li>
                  {t('shp.codesFromName', {
                    defaultValue:
                      'Cod primărie lipsă: {{count}} găsite în registru după numele primăriei și raion',
                    count: parsed.codesFromName,
                  })}
                </li>
              )}
              {(parsed.duplicatesDropped > 0 || parsed.keysDisambiguated > 0) && (
                <li>
                  {t('shp.normKeys', {
                    defaultValue:
                      'Chei: {{dupes}} duplicate identice eliminate, {{disamb}} dezambiguizate',
                    dupes: parsed.duplicatesDropped,
                    disamb: parsed.keysDisambiguated,
                  })}
                </li>
              )}
            </ul>
          </div>

          {parsed.columns ? (
            <ImportColumnCheck
              columns={parsed.columns}
              defaultYear={Number(defaultYear) || null}
            />
          ) : null}

          <ImportProblems problems={parsed.errors} fileName="probleme-poligoane.csv" />

          {(parsed.editable ?? []).some((r) => r.missing.length > 0) ? (
            <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900 dark:border-sky-900/50 dark:bg-sky-900/20 dark:text-sky-200">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {t('edit.nextHint', {
                defaultValue:
                  '{{count}} rânduri au date lipsă (câmpuri obligatorii sau UCF). Le puteți completa la pasul următor, înainte de import.',
                count: (parsed.editable ?? []).filter((r) => r.missing.length > 0).length,
              })}
            </div>
          ) : null}

          {parsed.terenCoverageMissing && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {t('shp.terenCoverageWarning', {
                defaultValue:
                  'Niciun poligon UCF nu corespunde acestui strat de terenuri. Importați întâi stratul UCF, altfel poligoanele părinte se vor suprapune peste subdiviziunile lor și suprafețele vor fi numărate de două ori.',
              })}
            </div>
          )}

          <Card>
            <CardBody className="max-h-72 overflow-auto p-0">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500 dark:bg-slate-800">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">UCF Key</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Raion</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Primăria</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Entit. silv.</th>
                    <th className="px-2 py-1.5 text-left font-semibold">UCF</th>
                    <th className="px-2 py-1.5 text-left font-semibold">UA</th>
                    <th className="px-2 py-1.5 text-right font-semibold">ha</th>
                    <th className="px-2 py-1.5 text-right font-semibold">An</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.preview.map((r) => (
                    <tr
                      key={r.rawIndex}
                      className="border-t border-slate-100 dark:border-slate-800"
                    >
                      <td className="px-2 py-1.5 font-mono text-[11px] text-forest-700">
                        {r.projectUcfKey}
                      </td>
                      <td className="px-2 py-1.5">{r.raion}</td>
                      <td className="px-2 py-1.5">{r.primarie}</td>
                      <td className="px-2 py-1.5">{r.entitateSilvica ?? '—'}</td>
                      <td className="px-2 py-1.5 font-mono">{r.ucf}</td>
                      <td className="px-2 py-1.5 font-mono">{r.ua ?? '—'}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">
                        {r.suprafata.toFixed(2)}
                      </td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{r.anul}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep('upload')}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common:action.back')}
            </Button>
            <Button onClick={() => setStep('complete')}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Complete / edit rows */}
      {step === 'complete' && parsed && (
        <div className="space-y-4">
          <ShapefileEditStep
            rows={parsed.editable ?? []}
            edits={edits}
            onChange={setEdits}
            truncated={parsed.editableTruncated}
          />
          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep('preview')}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              {t('common:action.back')}
            </Button>
            <Button onClick={() => setStep('commit')}>
              {t('common:action.next')}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 4: Commit */}
      {step === 'commit' && parsed && (
        <Card>
          <CardBody className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                {t('commit.mode', { defaultValue: 'Modul de import' })}
              </label>
              <Select
                value={mode}
                onChange={(e) => setMode(e.target.value as ImportMode)}
                className="mt-1"
              >
                <option value="skip_invalid">
                  {t('commit.modeSkip', { defaultValue: 'Sari peste rânduri invalide' })}
                </option>
                <option value="all_or_nothing">
                  {t('commit.modeAll', { defaultValue: 'Tot sau nimic' })}
                </option>
              </Select>
            </div>
            <div className="flex justify-between">
              <Button variant="secondary" onClick={() => setStep('complete')}>
                <ArrowLeft className="mr-1.5 h-4 w-4" />
                {t('common:action.back')}
              </Button>
              <Button onClick={onCommit} loading={commitMut.isPending}>
                {t('common:action.confirm')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Step 5: Done */}
      {step === 'done' && committed && (
        <Card>
          <CardBody className="space-y-3 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-forest-100 text-forest-600">
              <CheckCircle2 className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {t('done.title', { defaultValue: 'Import finalizat' })}
            </h2>
            <p className="text-sm text-slate-600">
              {t('done.summary', {
                defaultValue: 'Importate: {{imported}} · Sărit: {{skipped}}',
                imported: committed.imported,
                skipped: committed.skipped,
              })}
            </p>
            {committed.errors.length > 0 && (
              <div className="pt-2 text-left">
                <ImportProblems
                  problems={committed.errors.map((e) => ({
                    rawIndex: e.row,
                    code: 'commit',
                    reason: e.message,
                  }))}
                  fileName="probleme-salvare.csv"
                />
              </div>
            )}
            {(committed.updated ?? 0) + (committed.unchanged ?? 0) > 0 && (
              <p className="text-sm text-slate-600">
                {t('done.refreshed', {
                  defaultValue:
                    'Deja existente: {{updated}} actualizate din fișier (contur / suprafață), {{unchanged}} neschimbate',
                  updated: committed.updated ?? 0,
                  unchanged: committed.unchanged ?? 0,
                })}
              </p>
            )}
            <div className="flex justify-center gap-3 pt-2">
              <Button variant="secondary" onClick={() => navigate('/app/parcels')}>
                {t('done.viewParcels', { defaultValue: 'Vezi poligoane' })}
              </Button>
              <Button
                onClick={() => {
                  setFiles([]);
                  setParsed(null);
                  setCommitted(null);
                  setStep('upload');
                }}
              >
                {t('done.again', { defaultValue: 'Import nou' })}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {(parseMut.isPending || commitMut.isPending) && (
        <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
          <Spinner /> {t('common:state.loading', { defaultValue: 'Se procesează…' })}
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color: 'slate' | 'green' | 'red';
}) {
  const colorClass = {
    slate: 'bg-slate-50 text-slate-700 border-slate-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  }[color];
  return (
    <div className={cn('rounded-lg border px-3 py-2', colorClass)}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-0.5 text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}
